<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { messageStamp } from 'shared/helpers/timeHelper';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AudioPlayer from 'dashboard/components-next/audio/AudioPlayer.vue';
import SaasCallsAPI from 'dashboard/api/saas/calls';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import CallStatusBadge from './CallStatusBadge.vue';
import { getCallKind, formatCallDuration } from './constants';

const props = defineProps({
  call: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'transcribed']);

const { t } = useI18n();
const route = useRoute();

const dialogRef = ref(null);

onMounted(() => dialogRef.value?.open());

const kind = computed(() => getCallKind(props.call));

const contactName = computed(
  () => props.call.contact.name || props.call.contact.phoneNumber || ''
);

const directionLabel = computed(() =>
  props.call.direction === VOICE_CALL_DIRECTION.OUTBOUND
    ? t('CALLS_PAGE.DETAILS.OUTBOUND')
    : t('CALLS_PAGE.DETAILS.INBOUND')
);

const durationLabel = computed(
  () => formatCallDuration(props.call.durationSeconds) || '—'
);

const stampOf = time => (time ? messageStamp(time, 'MMM d yyyy, h:mm a') : '—');

const conversationRoute = computed(() => ({
  name: 'inbox_conversation',
  params: {
    accountId: route.params.accountId,
    conversation_id: props.call.conversation.displayId,
  },
  query: { messageId: props.call.messageId },
}));

const transcript = ref(props.call.transcript || '');
const isTranscribing = ref(false);

const transcribe = async () => {
  isTranscribing.value = true;
  try {
    const { data } = await SaasCallsAPI.transcribe(props.call.id);
    transcript.value = data.transcript;
    emit('transcribed', data.transcript);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CALLS_PAGE.DETAILS.TRANSCRIBE_ERROR')
    );
  } finally {
    isTranscribing.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CALLS_PAGE.DETAILS.TITLE')"
    width="xl"
    overflow-y-auto
    :show-confirm-button="false"
    :cancel-button-label="t('CALLS_PAGE.DETAILS.CLOSE')"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-5">
      <div class="flex items-center gap-3">
        <Avatar
          :src="call.contact.avatar"
          :name="contactName"
          :size="40"
          rounded-full
        />
        <div class="flex flex-col min-w-0">
          <span class="text-heading-3 font-medium text-n-slate-12 truncate">
            {{ contactName }}
          </span>
          <span class="text-body-main text-n-slate-11 truncate">
            {{ call.contact.phoneNumber }}
          </span>
        </div>
        <CallStatusBadge :kind="kind" class="ms-auto shrink-0" />
      </div>

      <div class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.DIRECTION') }}
          </span>
          <span class="text-body-main text-n-slate-12">
            {{ directionLabel }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.DURATION') }}
          </span>
          <span class="text-body-main text-n-slate-12 tabular-nums">
            {{ durationLabel }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.INBOX') }}
          </span>
          <span class="text-body-main text-n-slate-12 truncate">
            {{ call.inbox.name }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.AGENT') }}
          </span>
          <span class="text-body-main text-n-slate-12 truncate">
            {{ call.agent?.name || '—' }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.CREATED_AT') }}
          </span>
          <span class="text-body-main text-n-slate-12 tabular-nums">
            {{ stampOf(call.createdAt) }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.STARTED_AT') }}
          </span>
          <span class="text-body-main text-n-slate-12 tabular-nums">
            {{ stampOf(call.startedAt) }}
          </span>
        </div>
        <div v-if="call.endReason" class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.END_REASON') }}
          </span>
          <span class="text-body-main text-n-slate-12">
            {{ call.endReason }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-label-small text-n-slate-10">
            {{ t('CALLS_PAGE.DETAILS.CONVERSATION') }}
          </span>
          <RouterLink
            :to="conversationRoute"
            class="inline-flex items-center gap-1 text-body-main text-n-slate-12 hover:underline"
            @click="emit('close')"
          >
            <Icon
              icon="i-lucide-message-circle"
              class="size-3.5 text-n-slate-11"
            />
            {{ call.conversation.displayId }}
            <Icon
              icon="i-lucide-arrow-up-right"
              class="size-3.5 text-n-slate-11"
            />
          </RouterLink>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-label-small text-n-slate-10">
          {{ t('CALLS_PAGE.DETAILS.RECORDING') }}
        </span>
        <AudioPlayer
          v-if="call.recordingUrl"
          :src="call.recordingUrl"
          :fallback-duration="call.durationSeconds || 0"
          class="w-full"
        />
        <span v-else class="text-body-main text-n-slate-11">
          {{ t('CALLS_PAGE.DETAILS.NO_RECORDING') }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-label-small text-n-slate-10">
          {{ t('CALLS_PAGE.DETAILS.TRANSCRIPT') }}
        </span>
        <p
          v-if="transcript"
          class="text-body-main text-n-slate-12 whitespace-pre-wrap max-h-40 overflow-y-auto mb-0 bg-n-alpha-1 rounded-lg p-3"
        >
          {{ transcript }}
        </p>
        <Button
          v-else-if="call.recordingUrl"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-captions"
          :label="t('CALLS_PAGE.DETAILS.TRANSCRIBE')"
          :is-loading="isTranscribing"
          :disabled="isTranscribing"
          class="self-start"
          type="button"
          @click="transcribe"
        />
        <span v-else class="text-body-main text-n-slate-11">
          {{ t('CALLS_PAGE.DETAILS.NO_TRANSCRIPT') }}
        </span>
      </div>
    </div>
  </Dialog>
</template>
